/**
 * Oct 20 founder addendum S2-C — the Technician Passport's competency analytics.
 *
 * Three measurements, shown separately and never blended:
 *   1. Actual diagnostic activity — real completed-job counts and percentages.
 *      Never altered by the 5% weight or the 10% cap.
 *   2. Weighted evidence, before the cap — after the 0.05 inspection weight and
 *      evidence quality.
 *   3. Credited to the score, after the cap — how much the cap removed, and the
 *      parts that sum to the displayed competency score.
 *
 * Every figure is read from the stored derivation (`competencyAnalyticsFromRow`),
 * so what this shows is exactly what the score was computed from.
 *
 * Rendered INSIDE PassportDiagnosticCompetency, so it inherits that section's
 * audience exactly — it adds no visibility rule of its own.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { TechnicianDiagnosticStats } from '@/types/diagnosticIntelligence';
import {
  capReasonText,
  competencyAnalyticsFromRow,
  fmtNum,
  fmtPct,
  recalculationText,
} from '@/lib/passport/competencyAnalytics';

interface Props {
  /** The overall (category_key IS NULL) row. */
  overall: TechnicianDiagnosticStats | null | undefined;
}

function SplitBar({ inspectionPct, label }: { inspectionPct: number | null; label: string }) {
  if (inspectionPct === null) return null;
  const insp = Math.max(0, Math.min(100, inspectionPct));
  return (
    <div
      className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={label}
    >
      <div className="h-full bg-primary" style={{ width: `${100 - insp}%` }} />
      <div className="h-full bg-amber-400 dark:bg-amber-500" style={{ width: `${insp}%` }} />
    </div>
  );
}

function Row({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums text-right" data-testid={testId}>{value}</span>
    </div>
  );
}

export function PassportCompetencyAnalytics({ overall }: Props) {
  const [showMethod, setShowMethod] = useState(false);
  const v = competencyAnalyticsFromRow(overall);

  if (!v) {
    // A row computed before scoring version 2 has no breakdown. Say so rather
    // than invent one from figures that were derived under different rules.
    if (!overall) return null;
    return (
      <p className="text-xs text-muted-foreground border-t pt-3" data-testid="competency-analytics-legacy">
        The activity and contribution breakdown appears after this record's next recalculation.
      </p>
    );
  }

  const capPct = Math.round(v.inspectionCap * 100);
  const weight = v.inspectionWeight;
  const recalc = recalculationText(v.recalculation, v.scoringVersion);
  const window = v.windowDays ? `last ${v.windowDays} days` : 'evidence window';

  return (
    <section className="space-y-4 border-t pt-4" aria-label="Diagnostic competency analytics" data-testid="competency-analytics">
      {/* 1 ─ ACTUAL ACTIVITY */}
      <div className="space-y-2" data-testid="analytics-activity">
        <h4 className="text-sm font-medium">1. Actual diagnostic activity</h4>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-x-6">
          <Row label="Completed dedicated diagnostic jobs" value={String(v.activity.dedicatedJobs)} testId="activity-dedicated" />
          <Row label="Completed inspection jobs" value={String(v.activity.inspectionJobs)} testId="activity-inspection" />
          <Row label="Combined eligible jobs" value={String(v.activity.combinedJobs)} testId="activity-combined" />
          <Row
            label="Dedicated diagnostic / inspection"
            value={`${fmtPct(v.activity.dedicatedPct)} / ${fmtPct(v.activity.inspectionPct)}`}
            testId="activity-pct"
          />
        </div>
        <SplitBar
          inspectionPct={v.activity.inspectionPct}
          label={`Activity: ${fmtPct(v.activity.dedicatedPct)} dedicated diagnostic, ${fmtPct(v.activity.inspectionPct)} inspection`}
        />
        {v.activity.unclassifiedJobs > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="activity-unclassified">
            {v.activity.unclassifiedJobs} older captured job(s) could not be classified; they are recorded but not counted or scored.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Real job counts ({window}). Scoring weights and the cap never change these figures.
        </p>
      </div>

      {/* 2 ─ UNCAPPED WEIGHTED EVIDENCE */}
      <div className="space-y-2" data-testid="analytics-uncapped">
        <h4 className="text-sm font-medium">2. Weighted evidence (before the {capPct}% cap)</h4>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-x-6">
          <Row
            label="Dedicated diagnostic evidence"
            value={`${v.uncapped.dedicatedScored} × 1.00 = ${fmtNum(v.uncapped.diagnosticUnits)} units`}
            testId="uncapped-dedicated-units"
          />
          <Row
            label="Inspection evidence"
            value={`${v.uncapped.inspectionScored} × ${weight.toFixed(2)} = ${fmtNum(v.uncapped.inspectionUnits)} units`}
            testId="uncapped-inspection-units"
          />
          <Row label="Dedicated diagnostic quality" value={fmtNum(v.uncapped.diagnosticQuality, 1)} />
          <Row label="Inspection quality" value={fmtNum(v.uncapped.inspectionQuality, 1)} />
          <Row
            label="Contribution: dedicated / inspection"
            value={`${fmtPct(v.uncapped.dedicatedShare, 2)} / ${fmtPct(v.uncapped.inspectionShare, 2)}`}
            testId="uncapped-shares"
          />
        </div>
        <SplitBar inspectionPct={v.uncapped.inspectionShare} label={`Weighted contribution before the cap: ${fmtPct(v.uncapped.inspectionShare, 2)} inspection`} />
      </div>

      {/* 3 ─ FINAL CREDITED CONTRIBUTION */}
      <div className="space-y-2" data-testid="analytics-credited">
        <h4 className="text-sm font-medium">3. Credited to the competency score (after the cap)</h4>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-x-6">
          <Row
            label="Inspection units credited"
            value={`${fmtNum(v.credited.inspectionUnits)} of ${fmtNum(v.uncapped.inspectionUnits)}`}
            testId="credited-inspection-units"
          />
          <Row label="Removed by the cap" value={`${fmtNum(v.credited.unitsRemovedByCap)} units`} testId="credited-removed" />
          <Row
            label="Credited share: dedicated / inspection"
            value={`${fmtPct(v.credited.dedicatedShare, 2)} / ${fmtPct(v.credited.inspectionShare, 2)}`}
            testId="credited-shares"
          />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="credited-cap-reason">
          {capReasonText(v.credited.capReason, capPct)}
        </p>
        <div className="rounded-md bg-muted/50 p-3 space-y-1" data-testid="credited-composition">
          <Row label="From dedicated diagnostic evidence" value={`${v.credited.diagnosticPoints.toFixed(2)} pts`} testId="points-dedicated" />
          <Row label="From inspection evidence" value={`${v.credited.inspectionPoints.toFixed(2)} pts`} testId="points-inspection" />
          <Row label="Neutral (sample still building)" value={`${v.credited.neutralPoints.toFixed(2)} pts`} testId="points-neutral" />
          <div className="border-t pt-1">
            <Row label="Diagnostic competency score" value={v.credited.score.toFixed(2)} testId="points-total" />
          </div>
          <p className="text-xs text-muted-foreground">
            Inspection evidence is {fmtPct(v.credited.inspectionPctOfFinal)} of this score (at most {capPct}%).
          </p>
        </div>
      </div>

      {recalc && (
        <p className="text-xs text-muted-foreground" data-testid="analytics-recalculation">{recalc}</p>
      )}

      <button
        type="button"
        className="inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-sm text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={showMethod}
        onClick={() => setShowMethod((s) => !s)}
        data-testid="analytics-method-toggle"
      >
        {showMethod ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        How this is calculated
      </button>
      {showMethod && (
        <div className="text-xs text-muted-foreground space-y-1" data-testid="analytics-method">
          <p>
            Each completed inspection counts as {weight.toFixed(2)} of a comparable dedicated diagnostic job,
            before quality (confidence calibration, outcome, complaint confirmation, efficiency).
          </p>
          <p>
            Inspection evidence may make up at most {capPct}% of the credited evidence and of the score. With no
            dedicated diagnostic evidence it earns no credit — inspections alone never establish diagnostic competency.
          </p>
          <p>
            Until 50 dedicated diagnostic outcomes are recorded, part of the score stays neutral (50), so a small
            sample is never presented as established. Scoring version {v.scoringVersion}.
          </p>
        </div>
      )}
    </section>
  );
}
